import { ApolloError } from "@apollo/client/errors";
import ArticleIcon from "@mui/icons-material/Article";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BadgeIcon from "@mui/icons-material/Badge";
import ContactsIcon from "@mui/icons-material/Contacts";
import DeleteIcon from "@mui/icons-material/Delete";
import FactoryIcon from "@mui/icons-material/Factory";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import HelpIcon from "@mui/icons-material/Help";
import MapIcon from "@mui/icons-material/Map";
import SettingsIcon from "@mui/icons-material/Settings";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { FeatureFlagKey, type FeatureFlagMap } from "encamp-shared/src/featureFlags";
import { Permission } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useProducts } from "hooks/useProducts";
import { useTenant } from "hooks/useTenant";
import { useMemo } from "react";

import chemicalsTileIconDark from "../../assets/tile-icons/chemicals-dark.webp";
import chemicalsTileIcon from "../../assets/tile-icons/chemicals.webp";
import contactsTileIconDark from "../../assets/tile-icons/contacts-dark.webp";
import contactsTileIcon from "../../assets/tile-icons/contacts.webp";
import credentialsTileIconDark from "../../assets/tile-icons/credentials-dark.webp";
import credentialsTileIcon from "../../assets/tile-icons/credentials.webp";
import documentsTileIconDark from "../../assets/tile-icons/documents-dark.webp";
import documentsTileIcon from "../../assets/tile-icons/documents.webp";
import facilitiesTileIconDark from "../../assets/tile-icons/facilities-dark.webp";
import facilitiesTileIcon from "../../assets/tile-icons/facilities.webp";
import insightsTileIconDark from "../../assets/tile-icons/insights-dark.webp";
import insightsTileIcon from "../../assets/tile-icons/insights.webp";
import organizationsTileIcon from "../../assets/tile-icons/organizations.webp";
import scoutTileIcon from "../../assets/tile-icons/scout.webp";
import tasksTileIconDark from "../../assets/tile-icons/tasks-dark.webp";
import tasksTileIcon from "../../assets/tile-icons/tasks.webp";
import wasteTileIconDark from "../../assets/tile-icons/waste-dark.webp";
import wasteTileIcon from "../../assets/tile-icons/waste.webp";
import { NavSection, type NavItem } from "../../components/LeftNavV2/LeftNavDrawerV2";
import { type GroupChildNavItem, type StandardNavItem } from "../../components/LeftNavV2/NavItemV2";
import ScoutIcon from "../../components/ScoutIcon";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useStaffNavSectionsV2 } from "../Staff/staffNavSectionsV2";
import { createGroupChildNavItem, createStandardNavItem } from "./customerNavFlagGating";
import { hasScoutAccess } from "./Scout/scoutAccess";

export type CustomerNavBuilderOptions = {
  flags: Partial<FeatureFlagMap> | undefined;
  hasPermissions: (permissions: Permission[]) => boolean;
  isPartner: boolean;
  isStaff: boolean;
  staffNavSections: NavSection[];
  tenantId: string | undefined;
  hasProducts: boolean;
  productCatalogLabel: string;
};

export function buildCustomerNavSectionsV2({
  flags,
  hasPermissions,
  isPartner,
  isStaff,
  staffNavSections,
  tenantId,
  hasProducts,
  productCatalogLabel,
}: CustomerNavBuilderOptions): NavSection[] {
  if (!tenantId) {
    if (isStaff) {
      return staffNavSections;
    }

    return [];
  }

  const navItems: NavSection[] = [];
  const isChemicalsEnabled = Boolean(flags?.[FeatureFlagKey.TierIIModule]);
  const isWasteEnabled = Boolean(flags?.[FeatureFlagKey.WasteModule]);

  const chemicalsChildren: GroupChildNavItem[] = [
    createGroupChildNavItem(flags, { text: "Overview", path: `o/${tenantId}/chemicals/overview` }),
    createGroupChildNavItem(flags, {
      text: "Reports",
      path: `o/${tenantId}/chemicals/reports`,
      visible: hasPermissions([Permission.RouteEpcraReports]),
    }),
    createGroupChildNavItem(flags, {
      text: "Inventory",
      path: `o/${tenantId}/chemicals/inventory`,
    }),
    createGroupChildNavItem(flags, {
      text: "Chem Catalog",
      path: `o/${tenantId}/chemicals/catalog/chemicals`,
      visible: hasPermissions([Permission.RouteEpcraProducts]),
    }),
    createGroupChildNavItem(flags, {
      text: productCatalogLabel,
      path: `o/${tenantId}/chemicals/products`,
      visible: hasProducts && hasPermissions([Permission.RouteEpcraProducts]),
    }),
    createGroupChildNavItem(flags, {
      text: "Chem Contacts",
      path: `o/${tenantId}/chemicals/contacts`,
      visible: hasPermissions([Permission.RouteContacts]),
    }),
    createGroupChildNavItem(flags, {
      text: "Chem Documents",
      path: `o/${tenantId}/chemicals/documents`,
      visible: hasPermissions([Permission.RouteDocuments]),
    }),
  ].filter((item): item is GroupChildNavItem => item !== null);

  const wasteChildren: GroupChildNavItem[] = [
    createGroupChildNavItem(flags, { text: "Overview", path: `o/${tenantId}/waste/overview` }),
    createGroupChildNavItem(flags, {
      text: "Waste Facilities",
      path: `o/${tenantId}/waste/facilities`,
    }),
    createGroupChildNavItem(flags, {
      text: "Waste Streams",
      path: `o/${tenantId}/waste/streams`,
      // waste-streams-tenant-page removed: the page lives on the v2 surface, so it's
      // gated on manifest-views-v2 — which (being inDevelopment) also drives the
      // in-development preview icon, matching the rest of the v2 nav.
      visibilityFlag: FeatureFlagKey.ManifestViewsV2,
    }),
    createGroupChildNavItem(flags, {
      text: "Manifests",
      path: `o/${tenantId}/waste/manifests`,
      previewFlag: FeatureFlagKey.ManifestViewsV2,
    }),
    createGroupChildNavItem(flags, {
      text: "Line Items",
      path: `o/${tenantId}/waste/manifest-line-items`,
    }),
    createGroupChildNavItem(flags, {
      text: "Waste Contacts",
      path: `o/${tenantId}/waste/contacts`,
      visibilityFlag: FeatureFlagKey.RcraFacilityProfile,
      visible: hasPermissions([Permission.RouteContacts]),
    }),
    createGroupChildNavItem(flags, {
      text: "Submissions",
      path: `o/${tenantId}/waste/submissions`,
      visibilityFlag: FeatureFlagKey.WasteSubmissions,
    }),
  ].filter((item): item is GroupChildNavItem => item !== null);

  const programItems: NavItem[] = [
    {
      kind: "Group",
      text: "Chemicals",
      description:
        "Access Tier II reporting tools, inventories, chemical catalog, and program contacts/documents",
      Icon: WarehouseIcon,
      tileImage: chemicalsTileIcon,
      tileImageDark: chemicalsTileIconDark,
      path: isChemicalsEnabled ? `o/${tenantId}/chemicals/overview` : `o/${tenantId}/chemicals`,
      isExpandable: isChemicalsEnabled,
      children: isChemicalsEnabled ? chemicalsChildren : [],
    },
  ];

  programItems.push({
    kind: "Group",
    text: "Waste",
    description:
      "See your waste totals, individual facility details, track generator status and manifest information, and manage waste submissions",
    Icon: DeleteIcon,
    tileImage: wasteTileIcon,
    tileImageDark: wasteTileIconDark,
    path: isWasteEnabled ? `o/${tenantId}/waste/overview` : `o/${tenantId}/waste`,
    isExpandable: isWasteEnabled,
    children: isWasteEnabled ? wasteChildren : [],
  });

  if (programItems.length > 0) {
    navItems.push({
      title: "Programs",
      longTitle: "Program areas",
      items: programItems,
    });
  }

  const toolkitItems: NavItem[] = [
    createStandardNavItem(flags, {
      text: "Scout",
      description: "Explore Scout tools and recommendations for your organization",
      Icon: ScoutIcon,
      tileImage: scoutTileIcon,
      path: `o/${tenantId}/scout`,
      visibilityFlag: FeatureFlagKey.Scout,
      visible: hasScoutAccess({ flags, hasPermissions, isStaff }),
    }),
    createStandardNavItem(flags, {
      text: "Tasks",
      description:
        "Keep track of your compliance calendar with deadline tracking and custom reminders",
      Icon: AssignmentTurnedInIcon,
      tileImage: tasksTileIcon,
      tileImageDark: tasksTileIconDark,
      path: `o/${tenantId}/tasks`,
      visibilityFlag: FeatureFlagKey.TasksModule,
    }),
    createStandardNavItem(flags, {
      text: "Insights",
      description:
        "Understand what regulators see across your facilities in terms of compliance and benchmark your organization against peers",
      Icon: MapIcon,
      tileImage: insightsTileIcon,
      tileImageDark: insightsTileIconDark,
      path: `o/${tenantId}/insights`,
      visible: hasPermissions([Permission.RouteInsights]),
    }),
  ].filter((item): item is StandardNavItem => item !== null);

  if (toolkitItems.length > 0) {
    navItems.push({
      title: "Toolkit",
      longTitle: "Productivity Toolkit",
      items: toolkitItems,
    });
  }

  const catalogItems: NavItem[] = [
    ...(hasPermissions([Permission.RouteFacilities])
      ? [
          {
            text: "Facilities",
            description:
              "See information about your facilities and easily access program-specific information",
            kind: "Standard" as const,
            Icon: FactoryIcon,
            tileImage: facilitiesTileIcon,
            tileImageDark: facilitiesTileIconDark,
            path: `o/${tenantId}/facilities`,
          },
        ]
      : []),
    ...(hasPermissions([Permission.RouteContacts])
      ? [
          {
            text: "Contacts",
            description: "View and manage details about your contacts across programs",
            kind: "Standard" as const,
            Icon: ContactsIcon,
            tileImage: contactsTileIcon,
            tileImageDark: contactsTileIconDark,
            path: `o/${tenantId}/contacts`,
          },
        ]
      : []),
    ...(hasPermissions([Permission.RouteDocuments])
      ? [
          {
            text: "Documents",
            description: "Access documents across your facilities to support your compliance",
            kind: "Standard" as const,
            Icon: ArticleIcon,
            tileImage: documentsTileIcon,
            tileImageDark: documentsTileIconDark,
            path: `o/${tenantId}/documents`,
          },
        ]
      : []),
    ...(hasPermissions([Permission.RouteEpcraCredentials])
      ? [
          {
            text: "Credentials",
            description: "View, edit, and manage your credentials to various portals and systems",
            kind: "Standard" as const,
            Icon: BadgeIcon,
            tileImage: credentialsTileIcon,
            tileImageDark: credentialsTileIconDark,
            path: `o/${tenantId}/credentials`,
          },
        ]
      : []),
    ...(isPartner
      ? [
          {
            text: "Organizations",
            description: "Manage and view your partner organizations.",
            kind: "Standard" as const,
            Icon: GroupsRoundedIcon,
            tileImage: organizationsTileIcon,
            path: `o/${tenantId}/organizations`,
          },
        ]
      : []),
  ];

  if (catalogItems.length > 0) {
    navItems.push({ title: "Catalogs", items: catalogItems });
  }

  const setupItems: NavItem[] = [
    ...(hasPermissions([Permission.WriteAllUserTenant])
      ? [
          {
            kind: "Standard" as const,
            text: "Account",
            Icon: SettingsIcon,
            path: `o/${tenantId}/account/users`,
            isDisabled: false,
          },
        ]
      : []),
    {
      kind: "Standard",
      text: "Help",
      Icon: HelpIcon,
      path: "http://support.encamp.com",
      isDisabled: false,
      isExternal: true,
    },
  ];

  if (setupItems.length > 0) {
    navItems.push({ title: "Setup", items: setupItems });
  }

  if (isStaff) {
    navItems.push(...staffNavSections);
  }

  return navItems;
}

export function useCustomerNavSectionsV2(): {
  error: ApolloError | undefined;
  navItems: NavSection[];
} {
  const { isStaff, isPartner, isEmulating } = useCurrentUser();
  const { hasPermissions } = useAuthorization();
  const { error, tenant, isLKQ } = useTenant();
  const { hasProducts } = useProducts();
  const { featureFlags: flags } = useFeatureFlags();
  const staffNavSections = useStaffNavSectionsV2();

  const navItems = useMemo<NavSection[]>(() => {
    return buildCustomerNavSectionsV2({
      flags,
      hasPermissions,
      isPartner,
      isStaff: isStaff && !isEmulating,
      staffNavSections,
      tenantId: tenant?.id,
      hasProducts: Boolean(hasProducts),
      productCatalogLabel: isLKQ ? "Products / Equipment Catalog" : "Product Catalog",
    });
  }, [
    flags,
    hasPermissions,
    hasProducts,
    isEmulating,
    isLKQ,
    isPartner,
    isStaff,
    staffNavSections,
    tenant?.id,
  ]);

  return { error, navItems };
}
